// admin/src/pages/GatewayMonitoring/components/GatewayHealthCards.js
'use strict';

import React, { useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Skeleton,
  Button,
  Stack,
  Tooltip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RouterIcon from '@mui/icons-material/Router';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MemoryIcon from '@mui/icons-material/Memory';
import SpeedIcon from '@mui/icons-material/Speed';
import StorageIcon from '@mui/icons-material/Storage';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AddIcon from '@mui/icons-material/Add';

function formatRelativeTime(dateInput) {
  if (!dateInput) return 'Never checked in';
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return 'Unknown';
  const now = new Date();
  const diffSec = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffSec < 10) return 'Just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour}h ago`;
  const diffDays = Math.floor(diffHour / 24);
  return `${diffDays}d ago`;
}

function formatUptime(uptimeSec) {
  if (typeof uptimeSec !== 'number' || isNaN(uptimeSec) || uptimeSec <= 0) return '—';
  const d = Math.floor(uptimeSec / 86400);
  const h = Math.floor((uptimeSec % 86400) / 3600);
  const m = Math.floor((uptimeSec % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function getStatusSx(status, mode) {
  const norm = String(status || '').toLowerCase();
  switch (norm) {
    case 'online':
      return {
        color: mode === 'dark' ? '#2FD3B8' : '#007A68',
        bgcolor: mode === 'dark' ? 'rgba(0, 168, 143, 0.18)' : '#E6F7F4',
        border: '1px solid',
        borderColor: mode === 'dark' ? 'rgba(0, 168, 143, 0.4)' : '#A3E3D8',
        fontWeight: 700,
      };
    case 'degraded':
      return {
        color: mode === 'dark' ? '#FFC24B' : '#92400E',
        bgcolor: mode === 'dark' ? 'rgba(240, 162, 2, 0.18)' : '#FFF9E6',
        border: '1px solid',
        borderColor: mode === 'dark' ? 'rgba(240, 162, 2, 0.4)' : '#FDE68A',
        fontWeight: 700,
      };
    case 'offline':
      return {
        color: mode === 'dark' ? '#FF6B72' : '#991B1B',
        bgcolor: mode === 'dark' ? 'rgba(214, 50, 58, 0.18)' : '#FDF2F2',
        border: '1px solid',
        borderColor: mode === 'dark' ? 'rgba(214, 50, 58, 0.4)' : '#F8B4B7',
        fontWeight: 700,
      };
    case 'revoked':
    default:
      return {
        color: mode === 'dark' ? '#93A4BD' : '#4B5563',
        bgcolor: mode === 'dark' ? 'rgba(107, 122, 144, 0.18)' : '#F3F4F6',
        border: '1px solid',
        borderColor: mode === 'dark' ? 'rgba(107, 122, 144, 0.4)' : '#E5E7EB',
        fontWeight: 600,
      };
  }
}

export default function GatewayHealthCards({
  gateways = [],
  loading = false,
  mode = 'light',
  canCreate = false,
  canUpdate = false,
  canDelete = false,
  onAddGateway,
  onRenameGateway,
  onRevokeGateway,
  onDeleteGateway,
}) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [activeGateway, setActiveGateway] = useState(null);

  const handleOpenMenu = (event, gw) => {
    setMenuAnchor(event.currentTarget);
    setActiveGateway(gw);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
    setActiveGateway(null);
  };

  if (loading) {
    return (
      <Grid container spacing={2.5}>
        {[1, 2, 3].map((i) => (
          <Grid item xs={12} md={6} lg={4} key={i}>
            <Card variant="outlined" sx={{ p: 2.5 }}>
              <Skeleton variant="text" width="60%" height={32} />
              <Skeleton variant="rectangular" width="100%" height={160} sx={{ mt: 2, borderRadius: 1 }} />
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  if (gateways.length === 0) {
    return (
      <Card
        variant="outlined"
        sx={{
          p: 6,
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 320,
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: 'action.hover',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <RouterIcon sx={{ fontSize: 36, color: 'text.secondary' }} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          No Gateways Connected
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 440, mb: 3 }}>
          Connect an on-premise Active Directory gateway by issuing an enrollment token. Gateways report health telemetry and shipped events outbound to the cloud.
        </Typography>
        {canCreate && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={onAddGateway}>
            Add Gateway
          </Button>
        )}
      </Card>
    );
  }

  return (
    <>
      <Grid container spacing={2.5} data-testid="gateway-cards-container">
        {gateways.map((gw) => {
          const isRevoked = String(gw.status).toLowerCase() === 'revoked';
          const hasDroppedEvents = Number(gw.droppedEvents || 0) > 0;
          const isCbOpen = String(gw.circuitBreakerState).toLowerCase() === 'open';

          return (
            <Grid item xs={12} md={6} lg={4} key={gw._id}>
              <Card
                variant="outlined"
                data-testid={`gateway-card-${gw._id}`}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: mode === 'dark' ? '0 4px 16px rgba(0,0,0,0.4)' : '0 4px 16px rgba(0,0,0,0.06)',
                  },
                }}
              >
                <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  {/* Card Header: Name + Status Chip + Action Menu */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ minWidth: 0, flex: 1, pr: 1 }}>
                      <Typography
                        variant="h6"
                        noWrap
                        title={gw.name}
                        sx={{ fontWeight: 700, fontSize: '1.05rem' }}
                      >
                        {gw.name || 'Unnamed Gateway'}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                        <Chip
                          size="small"
                          label={gw.status || 'Offline'}
                          sx={getStatusSx(gw.status, mode)}
                        />
                        {gw.mockDirectory && (
                          <Chip
                            size="small"
                            label="MOCK DIRECTORY"
                            sx={{
                              fontSize: '0.65rem',
                              fontWeight: 800,
                              bgcolor: mode === 'dark' ? 'rgba(226, 98, 43, 0.2)' : '#FFF0E6',
                              color: mode === 'dark' ? '#F07C46' : '#C2410C',
                              border: '1px solid',
                              borderColor: mode === 'dark' ? '#E2622B' : '#FDBA74',
                            }}
                          />
                        )}
                      </Stack>
                    </Box>

                    {(canUpdate || canDelete) && (
                      <IconButton
                        size="small"
                        aria-label={`Actions for ${gw.name}`}
                        data-testid={`gateway-actions-btn-${gw._id}`}
                        onClick={(e) => handleOpenMenu(e, gw)}
                        sx={{ color: 'text.secondary' }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>

                  {/* Heartbeat timestamps */}
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Tooltip title={gw.lastHeartbeatAt ? new Date(gw.lastHeartbeatAt).toUTCString() : 'Never'}>
                      <Typography variant="caption" color="text.secondary">
                        Heartbeat: <strong>{formatRelativeTime(gw.lastHeartbeatAt)}</strong>
                        {gw.lastHeartbeatAt && ` (${new Date(gw.lastHeartbeatAt).toLocaleTimeString()})`}
                      </Typography>
                    </Tooltip>
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  {/* Telemetry Grid */}
                  <Grid container spacing={1.5} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Version
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {gw.version ? `v${gw.version}` : '—'}
                      </Typography>
                    </Grid>

                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Uptime
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatUptime(gw.uptimeSec)}
                      </Typography>
                    </Grid>

                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Memory RSS
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {typeof gw.memoryRssMb === 'number' ? `${gw.memoryRssMb} MB` : '—'}
                      </Typography>
                    </Grid>

                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Active Directory
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {gw.directoryReachable === true ? (
                          <>
                            <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                              Reachable
                            </Typography>
                          </>
                        ) : gw.directoryReachable === false ? (
                          <>
                            <CancelIcon sx={{ fontSize: 16, color: 'error.main' }} />
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
                              Unreachable
                            </Typography>
                          </>
                        ) : (
                          <>
                            <HelpOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                              Unknown
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Grid>

                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Circuit Breaker
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: isCbOpen ? 'error.main' : 'text.primary',
                        }}
                      >
                        {String(gw.circuitBreakerState || 'closed').toUpperCase()}
                      </Typography>
                    </Grid>

                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Spool Buffer
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {gw.bufferedEvents ?? 0} buffered
                      </Typography>
                    </Grid>
                  </Grid>

                  {/* Warning tag if dropping events */}
                  {hasDroppedEvents && (
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        bgcolor: mode === 'dark' ? 'rgba(214, 50, 58, 0.15)' : '#FEE2E2',
                        border: '1px solid',
                        borderColor: mode === 'dark' ? '#D6323A' : '#FCA5A5',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mt: 'auto',
                      }}
                    >
                      <WarningAmberIcon sx={{ fontSize: 18, color: 'error.main' }} />
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'error.main' }}>
                        {gw.droppedEvents} events dropped by gateway spool!
                      </Typography>
                    </Box>
                  )}

                  {/* Revocation info if revoked */}
                  {isRevoked && (
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        bgcolor: 'action.hover',
                        mt: 'auto',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Revoked: {gw.revokedAt ? new Date(gw.revokedAt).toLocaleString() : 'Yes'}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Gateway Action Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {canUpdate && (
          <MenuItem
            onClick={() => {
              const gw = activeGateway;
              handleCloseMenu();
              onRenameGateway && onRenameGateway(gw);
            }}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Rename Gateway</ListItemText>
          </MenuItem>
        )}

        {canDelete && activeGateway && String(activeGateway.status).toLowerCase() !== 'revoked' && (
          <MenuItem
            onClick={() => {
              const gw = activeGateway;
              handleCloseMenu();
              onRevokeGateway && onRevokeGateway(gw);
            }}
            sx={{ color: 'warning.main' }}
          >
            <ListItemIcon sx={{ color: 'warning.main' }}>
              <BlockIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Revoke Credentials</ListItemText>
          </MenuItem>
        )}

        {canDelete && (
          <MenuItem
            onClick={() => {
              const gw = activeGateway;
              handleCloseMenu();
              onDeleteGateway && onDeleteGateway(gw);
            }}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon sx={{ color: 'error.main' }}>
              <DeleteOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete Gateway</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
